// ============================================================================
// VOCABULAIRE — le mot de chaque champ d'article, par type de commerce et par langue.
//
// ⚠️ LA MAQUETTE N'A PAS CET ÉCRAN. « Super Admin Orgazone v2 » ne montre que le cas d'usage à garder en
//    tête — un type de commerce « Cave — Millésimes plutôt que saisons, aucune grille » — jamais l'écran
//    qui le rend possible. Ce fichier suit donc les conventions de son voisin Référentiels : cartes
//    `dcard`, tableaux `TableScroll`, édition en ligne, contrôles natifs, `run(fn, okMsg)`.
//
// LA CHAÎNE QUE L'ÉCRAN ÉDITE (docs/CADRAGE-Referentiels-Vocabulaire.md §6ter) :
//   ①  champ + métier  →  TERME      panneau « Par métier » — LE geste de l'écran
//   ②  terme + langue  →  TEXTE      le dictionnaire, replié sous « Tous les mots »
//   ③  la surcharge du commerçant    au back-office, jamais ici
// Ce qui descend dans la base d'un magasin est le TEXTE résolu ①+②, sous `vocab_default.<champ>.<langue>`,
// par la PROPAGATION (troisième panneau). Une cascade, pas une copie : la propagation réécrit
// `vocab_default.*` et n'approche jamais `vocab.*`, la case que le commerçant s'est appropriée.
//
// UN SEUL GESTE, DEPUIS LA LIGNE DU CHAMP. Le menu d'un champ propose les mots du dictionnaire et
// « Autre mot… », qui crée le mot ET le retient pour ce métier dans la foulée ; « Modifier » corrige le
// mot retenu depuis la même ligne. La mécanique (un terme, puis ses textes, puis le métier qui le retient)
// reste celle du serveur, mais l'écran la présente dans le sens où l'on pense : je choisis un mot.
// ⚠️ LA CLÉ TECHNIQUE N'EST JAMAIS TAPÉE : le serveur la dérive du mot français et la rend. La clé d'un
//    terme CRÉÉ ne se voit que dans la liste repliée — la vue avancée — parce qu'elle nomme le terme dans
//    la définition de chaque métier, et qu'un opérateur qui doit l'inventer expose la mécanique à
//    l'envers. La clé du CHAMP (`season`), elle, reste affichée en ① parce qu'elle nomme la colonne.
// ⚠️ UN MOT FRANÇAIS DÉJÀ CONNU EST RÉUTILISÉ, JAMAIS DUPLIQUÉ : c'est ce qui fait qu'ajouter un métier
//    n'ajoute aucune traduction. La saisie le dit avant d'envoyer, et n'envoie alors aucune création.
// ⚠️ LANGUE PIVOT ≠ LANGUE D'AFFICHAGE. Le serveur dérive la clé du texte en `DEFAULT_VOCABULARY_LANG`
//    (contrat) : tout ce qui est STRUCTUREL — le dédoublonnage, le verrouillage des autres langues quand
//    un mot existe, la case qui prend le focus et ses suggestions, l'ordre des cases — suit cette langue.
//    Ce que l'écran DIT (libellés de ligne, toasts, options du menu) suit `UI_LANG`, la langue de la
//    Console. Les deux valent « fr » aujourd'hui ; les confondre figerait la seconde dans la première.
//
// ⚠️ LE MOT D'UN CHAMP EST CELUI DU DICTIONNAIRE, JAMAIS LE FILET DU CODE. Le terme de plateforme d'un
//    champ a pour clé la clé du champ, et ses textes vivent en base : c'est CE texte que le serveur fait
//    descendre. `field.platform` — le filet — n'est que son repli quand ce texte manque. Un tableau qui
//    rendrait le filet d'office montrerait l'ancien mot après une correction en base : il mentirait sur
//    ce qui descend, précisément là où l'on vient vérifier.
// ⚠️ AUCUN MOT DE CHAMP N'EST ÉCRIT ICI, et les langues sont lues de la donnée. Le catalogue vit dans le
//    contrat, côté serveur ; un mot recopié ici serait une seconde vérité — le défaut que le vocabulaire
//    supprime.
// ⚠️ UNE ÉDITION ICI VAUT POUR LES MAGASINS CRÉÉS ENSUITE ; les existants reçoivent par la propagation,
//    et par elle seule : une caisse en service tire le JOURNAL, pas la table `setting`. Seule une écriture
//    par le producteur de données maîtres redescend — c'est ce que fait le serveur derrière « Propager ».
// ============================================================================
import { Fragment, useEffect, useMemo, useState } from 'react';
import { DEFAULT_VOCABULARY_LANG, firstUsableWord, isUsableWord, parseVocabularySettingKey, termKeyFromWord } from '@orgazone/sync-contract';
import { alert, confirm, showToast, EmptyState, Tooltip } from '@orgazone/ui-web';
import {
  api, type VocabularyChange, type VocabularyField, type VocabularyLang, type VocabularyPropagationResult,
  type VocabularyPropagationStore, type VocabularyState, type VocabularyTerm, type VocabularyTrade,
} from '../api';
import { useAuth, isSuperadmin } from '../auth';
import { TableScroll } from '../table-scroll';

/** Pourquoi un texte vide ne s'enregistre pas : le trou ne se verrait qu'en magasin, sous la forme d'une clé. */
const INCOMPLETE_HINT = 'Un mot est complet dans toutes les langues, sinon une clé s’afficherait en magasin.';
/**
 * La valeur du menu qui ouvre la saisie d'un mot nouveau. Hors de l'alphabet d'une clé de terme (elle
 * commence par une minuscule), donc jamais confondue avec l'une d'elles.
 */
const OTHER_WORD = '__other__';
/** La liste de suggestions des mots de la langue pivot — un seul écran monté à la fois, un seul identifiant. */
const PIVOT_WORDS_LIST_ID = 'vocab-pivot-words';
/** Langue d'affichage de la Console — deviendra celle du compte (SPEC §22.1). */
const UI_LANG: VocabularyLang = 'fr';

type Texts = Partial<Record<VocabularyLang, string>>;
type TermsByKey = ReadonlyMap<string, VocabularyTerm>;
/** Une action de l'écran : elle dit son issue RÉELLE — toast en succès, dialogue avec le motif en échec. */
type Run = (fn: () => Promise<unknown>, okMsg: string) => Promise<boolean>;

/**
 * Les langues du vocabulaire, dans l'ordre où le serveur les rend — lues de la DONNÉE, jamais recopiées :
 * la liste (`VOCABULARY_LANGS`) vit dans le contrat, côté serveur, et une copie ici divergerait à la
 * première langue ajoutée. Champs et termes sont lus tous les deux : l'un des deux peut être vide.
 */
function langsOf(data: VocabularyState): VocabularyLang[] {
  const seen: VocabularyLang[] = [];
  for (const texts of [...data.fields.map((f) => f.platform), ...data.terms.map((t) => t.texts)]) {
    for (const lang of Object.keys(texts) as VocabularyLang[]) if (!seen.includes(lang)) seen.push(lang);
  }
  return seen;
}

/** Complet = un texte non blanc dans CHAQUE langue — la seule règle du dictionnaire. */
const complete = (texts: Texts, langs: VocabularyLang[]): boolean => langs.every((l) => isUsableWord(texts[l]));

/** Les textes taillés aux extrémités, prêts à partir — jamais un blanc enregistré comme un mot. */
const trimmed = (texts: Texts, langs: VocabularyLang[]): Record<VocabularyLang, string> => {
  const out = {} as Record<VocabularyLang, string>;
  for (const l of langs) out[l] = (texts[l] ?? '').trim();
  return out;
};

/**
 * Un mot ramené à sa graphie de comparaison : composition NFC (deux façons d'écrire « é » en Unicode),
 * apostrophe typographique ramenée à la droite, blancs repliés, casse ignorée — les variantes qui ne
 * changent pas le mot.
 */
const foldWord = (word: string): string =>
  word.normalize('NFC').replace(/\u2019/g, "'").replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Deux mots de la langue pivot sont le MÊME mot à la graphie près. C'est de ce mot que le serveur dérive
 * la clé : c'est donc sur lui que l'écran reconnaît un mot déjà là, pour le réutiliser.
 * ⚠️ ELLE NE PLIE PAS COMME LA CLÉ (`termKeyFromWord` : NFD, accents retirés) — plier par la clé ferait
 *    passer « Taillé » pour « Taille » et SUBSTITUERAIT un mot à un autre sans un mot. Deux mots distincts
 *    qui plient sur la même clé se DISENT dans la saisie (`nearWord`), ils ne se confondent pas ici.
 */
const sameWord = (a: string, b: string): boolean => foldWord(a) === foldWord(b);

/** Les langues avec la pivot en tête : c'est d'elle que la clé se dérive, c'est elle qu'on tape d'abord. */
const pivotFirst = (langs: VocabularyLang[]): VocabularyLang[] =>
  [...langs.filter((l) => l === DEFAULT_VOCABULARY_LANG), ...langs.filter((l) => l !== DEFAULT_VOCABULARY_LANG)];

/**
 * Le mot d'un champ dans une langue, tel que le serveur le fait DESCENDRE : le texte du dictionnaire pour
 * le terme de plateforme du champ — sa clé est celle du champ — et le filet du code (`field.platform`)
 * seulement quand ce texte manque ou est vide. La règle est `firstUsableWord` du contrat, celle de
 * `resolveTradeTexts` côté serveur — jamais un ternaire local qui finirait par en dire une autre.
 */
function platformWord(field: VocabularyField, lang: VocabularyLang, terms: TermsByKey): string {
  return firstUsableWord(field.platform[lang], terms.get(field.key)?.texts[lang]);
}

/**
 * Le texte qu'un magasin d'un métier verra pour ce champ, dans cette langue : celui du terme retenu,
 * sinon le mot de plateforme.
 * ⚠️ Un terme retenu que le dictionnaire ne connaît plus s'affiche par sa clé, marqué : c'est un trou que
 *    le serveur devrait refuser, et l'écran ne le rebouche pas en silence avec le filet.
 * ⚠️ Un texte VIDE en base fait descendre le filet côté serveur ; l'écran montre ce mot-là, MARQUÉ
 *    « (texte manquant) » — une cellule blanche cacherait le trou, le filet nu le ferait passer pour voulu.
 */
function resolvedWord(field: VocabularyField, lang: VocabularyLang, retained: string | undefined, terms: TermsByKey): string {
  if (!retained) return platformWord(field, lang, terms);
  const term = terms.get(retained);
  if (!term) return `${retained} (terme inconnu)`;
  return firstUsableWord(`${platformWord(field, lang, terms)} (texte manquant)`, term.texts[lang]);
}

/**
 * `vocab_default.season.fr` → « <mot de plateforme> · fr » : le champ par son mot du dictionnaire, la
 * langue telle quelle. La clé se DÉCOMPOSE par le contrat (`parseVocabularySettingKey`), jamais par un
 * découpage maison. Une clé qui ne se lit pas s'affiche BRUTE — masquer ce qu'on ne comprend pas
 * ferait croire à une propagation plus petite qu'elle n'est.
 */
function describeChange(name: string, fields: VocabularyField[], terms: TermsByKey): string {
  const key = parseVocabularySettingKey(name);
  const field = key ? fields.find((f) => f.key === key.field) : undefined;
  return field && key ? `${platformWord(field, UI_LANG, terms)} · ${key.lang}` : name;
}

const thSt = { fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', background: 'var(--ink-50)', padding: '7px 12px', textAlign: 'left' } as const;
const tdSt = { padding: '7px 12px', fontSize: 'var(--t-dense)', verticalAlign: 'middle' } as const;
const inputSt = { height: 27, padding: '0 8px', borderRadius: 'var(--r-xs)', border: '1px solid var(--teal-500)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-dense)', width: '100%' } as const;
const selectSt = { height: 27, padding: '0 8px', borderRadius: 'var(--r-xs)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-dense)', width: '100%', minWidth: 160 } as const;
const headSt = { padding: '14px 18px', borderBottom: '1px solid var(--ink200)', background: 'var(--head)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' } as const;
const noteSt = { fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 3, maxWidth: 640, lineHeight: 1.5 } as const;
const hintSt = { fontSize: 'var(--t-sub)', color: 'var(--ink500)' } as const;
const editorRowSt = { background: 'color-mix(in srgb, var(--teal-500) 6%, transparent)' } as const;

export function Vocabulaire() {
  const { user } = useAuth();
  const canWrite = isSuperadmin(user);
  const [data, setData] = useState<VocabularyState | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const terms: TermsByKey = useMemo(() => new Map((data?.terms ?? []).map((t) => [t.key, t])), [data]);

  const load = () => api.vocabulary().then((r) => { setData(r); setErr(null); }).catch((e) => setErr((e as Error).message));
  useEffect(() => { void load(); }, []);

  const run: Run = async (fn, okMsg) => {
    setBusy(true);
    let ok = false;
    try { await fn(); showToast(okMsg); ok = true; }
    catch (e) { void alert({ title: 'Action impossible', message: (e as Error).message }); }
    // ⚠️ Rechargé dans les DEUX issues : un geste en deux appels (créer le mot, puis le retenir) peut avoir
    //    écrit le premier quand le second échoue — l'écran montre alors ce qui EST, le mot créé compris,
    //    jamais ce qu'on a demandé.
    finally { await load(); setBusy(false); }
    return ok;
  };

  if (err) return <div className="page"><div className="err">Erreur : {err}</div></div>;
  if (!data) return <div className="page"><div className="muted">Chargement du vocabulaire…</div></div>;

  const langs = langsOf(data);

  return (
    <div className="page ozin">
      <div style={{ marginBottom: 16 }}>
        <h1 className="page-title">Vocabulaire</h1>
        <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3 }}>
          Le mot de chaque champ d'article, par type de commerce et par langue. Une édition ici vaut pour
          les magasins créés ENSUITE ; les magasins existants reçoivent par la propagation, en bas de page.
        </div>
      </div>

      {langs.length === 0 ? (
        <div className="dcard"><EmptyState icon="globe" title="Aucune langue" note="Le serveur n'a rendu ni champ ni terme : rien à éditer tant que le catalogue est vide." /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <TradePanel data={data} terms={terms} langs={langs} canWrite={canWrite} busy={busy} run={run} />
          <TermsPanel data={data} langs={langs} canWrite={canWrite} busy={busy} run={run} />
          <PropagationPanel fields={data.fields} terms={terms} canWrite={canWrite} />
          {!canWrite && <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>Lecture seule — l'édition du vocabulaire est réservée au super-admin.</div>}
        </div>
      )}
    </div>
  );
}

// ═══ ① PAR MÉTIER — pour chaque champ, le mot que ce type de commerce retient ═══════════════════════
//
// Le tableau rend les champs REÇUS du serveur, dans son ordre. Le rendu résolu (une colonne par langue)
// est ce qu'un magasin de ce métier recevra en `vocab_default.*` : le texte du mot retenu, sinon celui
// du mot de plateforme.

/** La saisie ouverte sous une ligne : un mot NOUVEAU (`termKey` null) ou les textes du mot retenu. */
interface Editor { fieldKey: string; termKey: string | null; texts: Texts }

function TradePanel({ data, terms, langs, canWrite, busy, run }: {
  data: VocabularyState; terms: TermsByKey; langs: VocabularyLang[]; canWrite: boolean; busy: boolean; run: Run;
}) {
  const [tradeCode, setTradeCode] = useState<string | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const trade: VocabularyTrade | null = data.trades.find((t) => t.code === tradeCode) ?? data.trades[0] ?? null;

  /** Le terme du dictionnaire dont le mot pivot est celui-ci — à RÉUTILISER plutôt qu'à créer une seconde fois. */
  const existingWord = (word: string | undefined): VocabularyTerm | undefined =>
    isUsableWord(word) ? data.terms.find((t) => sameWord(t.texts[DEFAULT_VOCABULARY_LANG], word)) : undefined;

  /**
   * Un terme dont le mot pivot plie sur la MÊME CLÉ que celui-ci sans être le même mot (« Millesime »
   * face à « Millésime ») : le serveur suffixera, un mot distinct naîtra. On le DIT, on n'impose rien —
   * c'est peut-être bien un autre mot.
   */
  const nearWord = (word: string | undefined): VocabularyTerm | undefined => {
    const key = isUsableWord(word) ? termKeyFromWord(word) : null;
    return key ? data.terms.find((t) => termKeyFromWord(t.texts[DEFAULT_VOCABULARY_LANG]) === key) : undefined;
  };

  /**
   * Retenir un mot pour ce champ — immédiat, et la carte ENTIÈRE des dérogations part avec (PUT) :
   * n'envoyer que le champ touché effacerait les autres. `create` = le mot à créer D'ABORD : c'est le
   * serveur qui rend sa clé, l'écran ne la connaît pas avant ; les deux appels vont dans le même geste,
   * pour que l'issue rapportée soit celle du geste entier.
   */
  const retain = (field: VocabularyField, termKey: string | null, create?: Record<VocabularyLang, string>): Promise<boolean> => {
    if (!trade) return Promise.resolve(false);
    const fieldWord = platformWord(field, UI_LANG, terms);
    const word = create ? create[UI_LANG] : termKey ? (terms.get(termKey)?.texts[UI_LANG] ?? termKey) : fieldWord;
    return run(async () => {
      const key = create ? (await api.createVocabularyTerm({ texts: create })).key : termKey;
      // ⚠️ Le mot de plateforme du champ n'est pas une dérogation — retenu, il vaut `null` explicite, comme la
      //    chaîne vide du menu. Une absence muette laisserait le serveur deviner.
      const deviation = key && key !== field.key ? key : null;
      await api.setTradeVocabulary(trade.code, { ...trade.fields, [field.key]: deviation });
    }, `« ${fieldWord} » se dit « ${word} » pour ${trade.label}.`);
  };

  const onSelect = (field: VocabularyField, value: string) => {
    if (value === OTHER_WORD) { setEditor({ fieldKey: field.key, termKey: null, texts: {} }); return; }
    void retain(field, value);
  };

  /** Le geste de la saisie ouverte : créer puis retenir, réutiliser, ou enregistrer les textes du mot retenu. */
  const submitEditor = async () => {
    // ⚠️ La touche Entrée ne connaît pas l'état du bouton : sans ce refus, deux Entrée pendant l'appel
    //    créent deux mots.
    if (busy) return;
    const field = editor ? data.fields.find((f) => f.key === editor.fieldKey) : undefined;
    if (!editor || !field) return;
    const termKey = editor.termKey;
    let ok: boolean;
    if (termKey !== null) {
      // ⚠️ Vérifié ICI aussi, pas seulement par l'état du bouton : la touche Entrée passe par ce chemin.
      if (!complete(editor.texts, langs)) return;
      ok = await run(() => api.updateVocabularyTerm(termKey, trimmed(editor.texts, langs)), `Mot « ${(editor.texts[UI_LANG] ?? '').trim()} » enregistré.`);
    } else {
      const existing = existingWord(editor.texts[DEFAULT_VOCABULARY_LANG]);
      if (existing) ok = await retain(field, existing.key);
      else if (complete(editor.texts, langs)) ok = await retain(field, null, trimmed(editor.texts, langs));
      else return;
    }
    if (ok) setEditor(null);
  };

  /** La ligne de saisie sous un champ : une case par langue, la pivot en premier avec ses suggestions. */
  const editorRow = (field: VocabularyField, open: Editor) => {
    const creating = open.termKey === null;
    const existing = creating ? existingWord(open.texts[DEFAULT_VOCABULARY_LANG]) : undefined;
    const near = creating && !existing ? nearWord(open.texts[DEFAULT_VOCABULARY_LANG]) : undefined;
    const prefix = creating ? 'Autre mot' : 'Modifier le mot';
    const title = creating
      ? `Autre mot pour « ${platformWord(field, UI_LANG, terms)} »`
      : `Modifier le mot « ${terms.get(open.termKey ?? '')?.texts[UI_LANG] ?? open.termKey} »`;
    // Un mot déjà connu verrouille ses autres langues : elles sont celles du dictionnaire, pas à ressaisir.
    const shown = (l: VocabularyLang) => (existing && l !== DEFAULT_VOCABULARY_LANG ? existing.texts[l] : open.texts[l] ?? '');
    const ready = !busy && (existing ? true : complete(open.texts, langs));
    return (
      <tr style={editorRowSt}>
        <td colSpan={langs.length + 2} style={tdSt}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 0' }}>
            <div style={{ fontSize: 'var(--t-dense)', fontWeight: 600 }}>{title}</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {pivotFirst(langs).map((l) => (
                <label key={l} style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 'var(--t-sub)', color: 'var(--ink500)', minWidth: 160, flex: 1 }}>
                  {l}
                  {/* Inertes pendant l'appel : une frappe qui atterrit dans un geste en cours n'a rien où aller. */}
                  <input aria-label={`${prefix} — ${l}`} autoFocus={l === DEFAULT_VOCABULARY_LANG} value={shown(l)}
                    disabled={busy || (!!existing && l !== DEFAULT_VOCABULARY_LANG)}
                    list={creating && l === DEFAULT_VOCABULARY_LANG ? PIVOT_WORDS_LIST_ID : undefined}
                    onChange={(e) => setEditor({ ...open, texts: { ...open.texts, [l]: e.target.value } })}
                    onKeyDown={(e) => { if (e.key === 'Enter') void submitEditor(); if (e.key === 'Escape') setEditor(null); }}
                    style={inputSt} />
                </label>
              ))}
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button className="btn btn-cta sm" disabled={!ready} onClick={() => void submitEditor()} style={{ width: 'auto' }}>{creating ? 'Retenir' : 'Enregistrer'}</button>
                <button className="ghost btn-ghost sm" style={{ width: 'auto' }} onClick={() => setEditor(null)}>Annuler</button>
              </div>
            </div>
            {existing && <div style={{ ...hintSt, color: 'var(--teal-600)' }}>« {existing.texts[UI_LANG]} » existe déjà — il sera réutilisé.</div>}
            {near && <div style={hintSt}>Proche de « {near.texts[UI_LANG]} », déjà au dictionnaire — un mot distinct sera créé.</div>}
            {!existing && !complete(open.texts, langs) && <div style={hintSt}>{INCOMPLETE_HINT}</div>}
          </div>
        </td>
      </tr>
    );
  };

  const deviations = trade ? Object.keys(trade.fields).length : 0;

  return (
    <section className="dcard" aria-label="Par métier" style={{ overflow: 'hidden' }}>
      <div style={headSt}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>Par métier</div>
          <div style={noteSt}>
            Le mot que chaque type de commerce retient pour un champ. Sans dérogation, le mot de la
            plateforme s'applique ; le rendu à droite est ce que recevra un magasin de ce métier.
          </div>
        </div>
        {data.trades.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-body)', color: 'var(--ink600)' }}>
            Type de commerce
            <select aria-label="Type de commerce" value={trade?.code ?? ''} onChange={(e) => { setTradeCode(e.target.value); setEditor(null); }} style={{ ...selectSt, width: 'auto' }}>
              {data.trades.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
            <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{deviations} dérogation{deviations > 1 ? 's' : ''}</span>
          </label>
        )}
      </div>
      {/* Les mots pivot déjà là, proposés à la frappe : celui qu'on retape est réutilisé, pas recréé. */}
      <datalist id={PIVOT_WORDS_LIST_ID}>{data.terms.map((t) => <option key={t.key} value={t.texts[DEFAULT_VOCABULARY_LANG]} />)}</datalist>

      {!trade ? (
        <EmptyState icon="store" title="Aucun type de commerce" note="Créez-en un dans Référentiels › Types de commerce : c'est lui qui retient les mots." />
      ) : data.fields.length === 0 ? (
        <EmptyState icon="grid" title="Aucun champ" note="Le serveur n'a rendu aucun champ : le catalogue du contrat est vide." />
      ) : (
        <TableScroll minWidth={720}>
          <thead>
            <tr>
              <th style={thSt}>Champ</th>
              <th style={thSt}>Mot retenu</th>
              {langs.map((l) => <th key={l} style={thSt}>{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.fields.map((field) => {
              const current = trade.fields[field.key] ?? '';
              const fieldWord = platformWord(field, UI_LANG, terms);
              const retained = terms.get(current || field.key);
              const open = editor?.fieldKey === field.key ? editor : null;
              return (
                <Fragment key={field.key}>
                  <tr className="dt-row">
                    <td style={tdSt}>
                      <span style={{ fontWeight: 700 }}>{fieldWord}</span>
                      <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)', marginLeft: 8 }}>{field.key}</span>
                    </td>
                    <td style={tdSt}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <select aria-label={`Mot pour ${fieldWord}`} value={current} disabled={!canWrite || busy}
                          onChange={(e) => onSelect(field, e.target.value)} style={selectSt}>
                          <option value="">Mot de plateforme — {fieldWord}</option>
                          {/* Le mot de plateforme du champ EST l'option vide : le lister aussi ferait deux entrées pour un mot. */}
                          {data.terms.filter((t) => t.key !== field.key).map((t) => <option key={t.key} value={t.key}>{t.texts[UI_LANG]}</option>)}
                          <option value={OTHER_WORD}>Autre mot…</option>
                        </select>
                        {canWrite && (
                          <Tooltip content={retained ? undefined : 'mot absent du dictionnaire'}>
                            <button className="ghost btn-ghost sm" disabled={busy || !retained} aria-label={`Modifier le mot pour ${fieldWord}`} style={{ width: 'auto' }}
                              onClick={() => retained && setEditor({ fieldKey: field.key, termKey: retained.key, texts: { ...retained.texts } })}>Modifier</button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                    {langs.map((l) => (
                      <td key={l} style={{ ...tdSt, color: current ? 'var(--ink900)' : 'var(--ink600)' }}>{resolvedWord(field, l, current || undefined, terms)}</td>
                    ))}
                  </tr>
                  {open && editorRow(field, open)}
                </Fragment>
              );
            })}
          </tbody>
        </TableScroll>
      )}
    </section>
  );
}

// ═══ ② TOUS LES MOTS — le dictionnaire entier, replié : la vue avancée, pour corriger ou supprimer ═══
//
// Un mot est l'unité de traduction : « millésime » se traduit une fois et sert à tous les métiers qui
// le retiennent. Les mots `builtin` sont la graine issue du catalogue — ils ne se suppriment pas, et
// un mot employé par un métier non plus : le refus se lit sur le bouton, pas dans un dialogue. C'est ici,
// et ici seulement, que la clé d'un terme CRÉÉ se montre — celle que le serveur a dérivée du mot
// français ; la clé du champ (`season`), elle, reste affichée en ① parce qu'elle nomme la colonne.
function TermsPanel({ data, langs, canWrite, busy, run }: {
  data: VocabularyState; langs: VocabularyLang[]; canWrite: boolean; busy: boolean; run: Run;
}) {
  const [editing, setEditing] = useState<{ key: string; texts: Texts } | null>(null);
  const [open, setOpen] = useState(false);

  /** Les métiers qui retiennent ce mot — c'est ce qui interdit sa suppression, et le bouton le dit. */
  const usedBy = (key: string): string[] =>
    data.trades.filter((t) => Object.values(t.fields).includes(key)).map((t) => t.label);

  const saveEdit = async () => {
    // ⚠️ La touche Entrée ne connaît pas l'état du bouton : sans ce refus, deux Entrée pendant l'appel
    //    envoient deux fois — et un texte vide est refusé ICI aussi, pour la même raison.
    if (busy) return;
    if (!editing || !complete(editing.texts, langs)) return;
    const ok = await run(() => api.updateVocabularyTerm(editing.key, trimmed(editing.texts, langs)), `Mot « ${(editing.texts[UI_LANG] ?? '').trim()} » enregistré.`);
    if (ok) setEditing(null);
  };
  const remove = async (term: VocabularyTerm) => {
    const ok = await confirm({
      title: `Supprimer le mot « ${term.texts[UI_LANG]} » ?`,
      message: 'Il disparaît du dictionnaire. Les magasins qui l’ont déjà reçu gardent leur mot jusqu’à la prochaine propagation.',
      confirmLabel: 'Supprimer',
    });
    if (ok) void run(() => api.deleteVocabularyTerm(term.key), `Mot « ${term.texts[UI_LANG]} » supprimé.`);
  };

  return (
    <section className="dcard" aria-label="Tous les mots" style={{ overflow: 'hidden' }}>
      <details onToggle={(e) => setOpen(e.currentTarget.open)}>
        <summary style={{ ...headSt, borderBottom: 'none', cursor: 'pointer', listStyle: 'none' }}>
          <span aria-hidden="true" style={{ color: 'var(--ink500)' }}>{open ? '▾' : '▸'}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>Tous les mots ({data.terms.length})</div>
            <div style={noteSt}>
              Le dictionnaire entier, pour corriger ou supprimer. Chaque mot est traduit une fois et sert à
              tous les métiers qui le retiennent ; un mot de plateforme se corrige, il ne se supprime pas.
              La clé d'un mot créé, dérivée du mot français par le serveur, ne s'affiche qu'ici.
            </div>
          </div>
        </summary>
        <div style={{ borderTop: '1px solid var(--ink200)' }}>
          {data.terms.length === 0 ? (
            <EmptyState icon="grid" title="Dictionnaire vide" note="Aucun mot : les magasins reçoivent le mot de la plateforme pour chaque champ." />
          ) : (
            <TableScroll minWidth={720}>
              <thead>
                <tr>
                  <th style={thSt}>Clé</th>
                  {langs.map((l) => <th key={l} style={thSt}>{l}</th>)}
                  {canWrite && <th style={thSt} />}
                </tr>
              </thead>
              <tbody>
                {data.terms.map((term) => {
                  const isEd = editing?.key === term.key;
                  const users = usedBy(term.key);
                  const lockWhy = term.builtin ? 'mot de plateforme' : users.length ? `employé par : ${users.join(', ')}` : null;
                  return (
                    <tr key={term.key} className="dt-row">
                      <td style={tdSt}>
                        <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{term.key}</span>
                        {term.builtin && <span style={{ fontSize: 'var(--t-eyebrow)', fontWeight: 700, padding: '2px 8px', marginLeft: 8, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink500)' }}>plateforme</span>}
                      </td>
                      {isEd && editing
                        ? langs.map((l) => (
                          <td key={l} style={tdSt}>
                            <input aria-label={`${term.key} — ${l}`} value={editing.texts[l] ?? ''} disabled={busy} onChange={(e) => setEditing({ ...editing, texts: { ...editing.texts, [l]: e.target.value } })}
                              onKeyDown={(e) => { if (e.key === 'Enter') void saveEdit(); if (e.key === 'Escape') setEditing(null); }} style={inputSt} />
                          </td>
                        ))
                        : langs.map((l) => <td key={l} style={tdSt}>{term.texts[l]}</td>)}
                      {canWrite && (
                        <td style={{ ...tdSt, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {isEd && editing ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, flexWrap: 'wrap' }}>
                              {!complete(editing.texts, langs) && <span style={hintSt}>{INCOMPLETE_HINT}</span>}
                              <button className="btn btn-cta sm" disabled={busy || !complete(editing.texts, langs)} onClick={() => void saveEdit()} style={{ width: 'auto' }}>Enregistrer</button>
                              <button className="ghost btn-ghost sm" style={{ width: 'auto' }} onClick={() => setEditing(null)}>Annuler</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                              <button className="ghost btn-ghost sm" disabled={busy} aria-label={`Modifier ${term.key}`} style={{ width: 'auto' }}
                                onClick={() => setEditing({ key: term.key, texts: { ...term.texts } })}>Modifier</button>
                              {/* ⚠️ L'infobulle de la charte et non `title` : un bouton `disabled` n'affiche pas son `title` sur
                                  une partie des navigateurs — c'est-à-dire exactement ici, où seul le motif du refus compte. */}
                              <Tooltip content={lockWhy ?? undefined}>
                                <button className="ghost btn-ghost sm danger" disabled={busy || lockWhy !== null} aria-label={`Supprimer ${term.key}`} style={{ width: 'auto' }}
                                  onClick={() => void remove(term)}>Supprimer</button>
                              </Tooltip>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </TableScroll>
          )}
        </div>
      </details>
    </section>
  );
}

// ═══ ③ PROPAGATION AUX MAGASINS EXISTANTS — ce qui diffère, magasin par magasin, puis le geste ═════
//
// L'analyse ouvre la base de chaque magasin et ne liste que ce qui DIFFÈRE de `vocab_default.*` tel
// que le métier du magasin le résout aujourd'hui. « Propager » ne pousse que ces entrées, par le
// producteur de données maîtres du back-office — le seul chemin qu'une caisse en service voit passer.
function PropagationPanel({ fields, terms, canWrite }: { fields: VocabularyField[]; terms: TermsByKey; canWrite: boolean }) {
  const [stores, setStores] = useState<VocabularyPropagationStore[] | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<VocabularyPropagationResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /** Un magasin qu'on peut cocher : analysé sans erreur, et avec quelque chose à recevoir. */
  const actionable = (s: VocabularyPropagationStore) => !s.skipped && !s.error && s.changes.length > 0;

  const analyze = async () => {
    setBusy(true); setErr(null); setResults(null); setStores(null);
    try {
      const r = await api.analyzeVocabularyPropagation();
      setStores(r.stores);
      // Pré-cochés = ceux qui ont quelque chose à recevoir. Cocher un magasin à jour ferait croire
      // qu'une écriture l'attend.
      setSel(new Set(r.stores.filter(actionable).map((s) => s.id)));
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true); setErr(null);
    try { const r = await api.applyVocabularyPropagation([...sel]); setResults(r.results); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };
  const toggle = (id: string) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const changeLine = (c: VocabularyChange) => (
    <div key={c.name} style={{ display: 'flex', gap: 8, padding: '3px 0', fontSize: 'var(--t-sub)', flexWrap: 'wrap' }}>
      <span style={{ color: 'var(--ink600)', minWidth: 160 }}>{describeChange(c.name, fields, terms)}</span>
      <span className="mono" style={{ color: 'var(--ink500)' }}>{c.from ?? '—'}</span>
      <span style={{ color: 'var(--ink400)' }}>→</span>
      <span className="mono" style={{ color: 'var(--ink900)' }}>{c.to}</span>
    </div>
  );

  return (
    <section className="dcard" aria-label="Propagation aux magasins existants" style={{ overflow: 'hidden' }}>
      <div style={headSt}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>Propagation aux magasins existants</div>
          <div style={noteSt}>
            Une édition ici vaut pour les magasins créés ENSUITE ; les magasins existants reçoivent par
            cette propagation. Elle réécrit les défauts du métier, jamais ce que le commerçant a surchargé.
          </div>
        </div>
        <button onClick={() => void analyze()} disabled={!canWrite || busy}
          className="btn-ghost" style={{ height: 32, padding: '0 13px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-dense)', fontWeight: 600, ...(canWrite ? {} : { opacity: 0.5, cursor: 'not-allowed' }) }}>
          {busy && !stores ? 'Analyse…' : stores ? 'Analyser à nouveau' : 'Analyser'}
        </button>
      </div>

      {err && <div className="err" role="alert" style={{ padding: '10px 18px', marginTop: 0 }}>{err}</div>}
      {busy && !stores && !err && <div className="muted" style={{ padding: '12px 18px' }}>Analyse des bases magasin… (une connexion par magasin)</div>}
      {!stores && !busy && !err && <div style={{ padding: '12px 18px', fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>Lancez l'analyse : elle lit la table des réglages de chaque magasin et ne liste que ce qui diffère.</div>}

      {stores && !results && (
        <div style={{ padding: '8px 18px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {stores.length === 0 && <EmptyState icon="store" title="Aucun magasin" note="Rien à propager : aucun magasin actif avec une base." />}
          {stores.map((s) => {
            const n = s.changes.length;
            return (
              <div key={s.id} style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', padding: '9px 12px', opacity: s.skipped ? 0.6 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <input type="checkbox" aria-label={`Propager vers ${s.name}`} checked={sel.has(s.id)} disabled={!actionable(s) || busy} onChange={() => toggle(s.id)} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: 'var(--t-dense)', fontWeight: 700 }}>{s.name}</span>
                    <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginLeft: 8 }}>{s.gln}</span>
                    {/* Le métier n'a de sens que pour un magasin ANALYSÉ : un magasin sauté n'a pas été résolu, il a été sauté. */}
                    {!s.skipped && !s.error && <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginLeft: 8 }}>{s.trade ?? 'métier non résolu'}</span>}
                  </div>
                  {s.skipped && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{s.skipped}</span>}
                  {s.error && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--da-fg)' }}>{s.error}</span>}
                  {!s.skipped && !s.error && (
                    <span className="mono" style={{ fontSize: 'var(--t-sub)', color: n ? 'var(--ink700)' : 'var(--ink500)' }}>
                      {n === 0 ? 'à jour' : `${n} changement${n > 1 ? 's' : ''}`}
                    </span>
                  )}
                </div>
                {s.note && <div style={{ marginTop: 4, fontSize: 'var(--t-meta)', color: 'var(--wa-fg)' }}>{s.note}</div>}
                {n > 0 && (
                  <details style={{ marginTop: 6 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 'var(--t-sub)', color: 'var(--teal-600)' }}>Détail des {n} changement{n > 1 ? 's' : ''}</summary>
                    <div style={{ marginTop: 4 }}>{s.changes.map(changeLine)}</div>
                  </details>
                )}
              </div>
            );
          })}
          {stores.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
              <button className="btn btn-cta sm" disabled={busy || sel.size === 0} onClick={() => void apply()} style={{ width: 'auto' }}>
                {busy ? 'Propagation…' : `Propager vers ${sel.size} magasin${sel.size > 1 ? 's' : ''}`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Résultats RÉELS par magasin : ce que le producteur a poussé, ou l'erreur nommée — rien de présumé. */}
      {results && (
        <div style={{ padding: '8px 18px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {results.map((r) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 12px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)' }}>
              <span style={{ color: r.error ? 'var(--da-fg)' : 'var(--ok-fg)', flexShrink: 0 }}>{r.error ? '✕' : '✓'}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 'var(--t-dense)', fontWeight: 700 }}>{r.name}{r.gln && <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginLeft: 8 }}>{r.gln}</span>}</div>
                {r.error
                  ? <div style={{ fontSize: 'var(--t-note)', color: 'var(--da-fg)' }}>{r.error}</div>
                  : <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>
                      {r.pushed ?? 0} entrée{(r.pushed ?? 0) > 1 ? 's' : ''} poussée{(r.pushed ?? 0) > 1 ? 's' : ''}{r.note ? ` · ${r.note}` : ''}
                    </div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
